import React, { useState } from 'react';
import { ShieldCheckIcon, BookOpenIcon, HelpCircleIcon, SettingsIcon, PlayIcon, LayoutDashboardIcon } from 'lucide-react';
import { GovernanceOverviewTab } from './tabs/GovernanceOverviewTab';
import { GovernanceRulesTab } from './tabs/GovernanceRulesTab';
import { GovernanceHowItWorksTab } from './tabs/GovernanceHowItWorksTab';
import { GovernanceSettingsTab } from './tabs/GovernanceSettingsTab';
import { GovernanceValidationTab } from './tabs/GovernanceValidationTab';
type TabId = 'overzicht' | 'regels' | 'hoe-het-werkt' | 'validatie' | 'instellingen';
interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  description: string;
}
const TABS: Tab[] = [{
  id: 'overzicht',
  label: 'Overzicht',
  icon: <LayoutDashboardIcon className="h-4 w-4" data-id="element-3036" />,
  description: 'Dashboard met statistieken en pipeline-status'
}, {
  id: 'regels',
  label: 'Regels',
  icon: <ShieldCheckIcon className="h-4 w-4" data-id="element-3037" />,
  description: 'Alle governance-regels en hun beschrijvingen'
}, {
  id: 'hoe-het-werkt',
  label: 'Hoe het werkt',
  icon: <HelpCircleIcon className="h-4 w-4" data-id="element-3038" />,
  description: 'Uitleg over de pipeline, cognitieve niveaus en meer'
}, {
  id: 'validatie',
  label: 'Validatie',
  icon: <PlayIcon className="h-4 w-4" data-id="element-3039" />,
  description: 'Voer validatie uit en bekijk resultaten'
}, {
  id: 'instellingen',
  label: 'Instellingen',
  icon: <SettingsIcon className="h-4 w-4" data-id="element-3040" />,
  description: 'Configureer kwaliteitspoorten en beleidsregels'
}];
export function GovernancePage() {
  const [activeTab, setActiveTab] = useState<TabId>('overzicht');
  const currentTab = TABS.find(t => t.id === activeTab)!;
  return <div className="min-h-screen w-full bg-gray-50" data-id="element-3041">
      {/* Page Header */}
      <div className="border-b border-gray-200 bg-white" data-id="element-3042">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6" data-id="element-3043">
          <div className="flex items-start gap-4" data-id="element-3044">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-sm" data-id="element-3045">
              <ShieldCheckIcon className="h-6 w-6 text-white" data-id="element-3046" />
            </div>
            <div data-id="element-3047">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3048">Governance</h1>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-3049">
                Kwaliteitsbeheer en validatie van het vragenbestand
              </p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mt-6 flex gap-1 overflow-x-auto" data-id="element-3050">
            {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} aria-selected={activeTab === tab.id} role="tab" data-id="element-3051">
                <span className={activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'} data-id="element-3052">
                  {tab.icon}
                </span>
                {tab.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6" data-id="element-3053">
        {/* Breadcrumb / context */}
        <div className="mb-5" data-id="element-3054">
          <p className="text-xs text-gray-400" data-id="element-3055">
            Governance →{' '}
            <span className="text-gray-600 font-medium" data-id="element-3056">
              {currentTab.label}
            </span>
          </p>
          <p className="text-sm text-gray-500 mt-0.5" data-id="element-3057">
            {currentTab.description}
          </p>
        </div>

        {/* Tab Panels */}
        {activeTab === 'overzicht' && <GovernanceOverviewTab data-id="element-3058" />}
        {activeTab === 'regels' && <GovernanceRulesTab data-id="element-3059" />}
        {activeTab === 'hoe-het-werkt' && <GovernanceHowItWorksTab data-id="element-3060" />}
        {activeTab === 'validatie' && <GovernanceValidationTab data-id="element-3061" />}
        {activeTab === 'instellingen' && <GovernanceSettingsTab data-id="element-3062" />}
      </div>
    </div>;
}